import { EServerPath } from '~/models/api'
import { store } from '~/store'

export const IMPERSONATION_HEADERS = {
  user: 'x-impersonate-user',
  orgId: 'x-impersonate-orgid',
} as const

export function getImpersonationHeaders(): Record<string, string> {
  const { user } = store.getState()
  if (!user.impersonatorsUserId || !user.id) return {}
  // Both backends gate impersonation on the truthiness of both headers
  // (consent token), but never read the value of `x-impersonate-orgid` —
  // they always resolve the actual org from DB. When an admin impersonates
  // an org-less user, fall back to the user UUID so the gate passes; the
  // value is not used for any authorization decision downstream.
  return {
    [IMPERSONATION_HEADERS.user]: user.id,
    [IMPERSONATION_HEADERS.orgId]: user.organizationId || user.id,
  }
}

export type HttpServiceBaseResponse<T> = {
  data: T | null
}
export type HttpQueryValue = string | string[]

export type HttpRequestBase = {
  path: EServerPath | string
  query?: Record<string, HttpQueryValue>
  payload?: unknown
  headers?: Record<string, string>
  // Aborts the in-flight request. Honored by the axios-backed services only.
  // React Query supplies one per query; consuming it is what lets React Query
  // actually cancel a superseded fetch rather than just stop retrying it.
  signal?: AbortSignal
  // Optional upload-progress callback (honored by the axios service only).
  onUploadProgress?: (event: { loaded: number; total?: number }) => void
}

export default interface HttpServiceBase {
  get<T>({ path, query }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>>

  post<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>>

  put<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>>

  patch<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>>

  delete<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>>
}
